import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dropdown, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';

import {
    carFeatureListService,
    carFeatureCreateService,
    carFeatureUpdateService,
    carFeatureDeleteService,
    carFeatureByIdService
} from '../../services/carFeature.service';
import { featureCategoryDropdownService } from '../../services/featureCategory.service';
import { Loader } from '../../components/util/Svg';
import Breadcrumb from '../../components/Breadcrumb';
import { hasPermission, PERMISSIONS } from '../../config/permissions';
import { getDisplayMessage } from '../../helper';

const CarFeature = ({ adminProfile }) => {
    const [list, setList] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loader, setLoader] = useState(true);
    const [createModal, setCreateModal] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const [categoryId, setCategoryId] = useState('');
    const [categoryIdErr, setCategoryIdErr] = useState('');
    const [name, setName] = useState('');
    const [nameErr, setNameErr] = useState('');
    const [description, setDescription] = useState('');
    const [sortOrder, setSortOrder] = useState('0');
    const [status, setStatus] = useState('1');
    const [icon, setIcon] = useState('');
    const [viewData, setViewData] = useState(null);

    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [pageNo, setPageNo] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const [isDisabled, setIsDisabled] = useState(false);
    const [currentId, setCurrentId] = useState('');
    const [deleteId, setDeleteId] = useState('');

    const canView = hasPermission(PERMISSIONS.CAR_FEATURE.VIEW, adminProfile);
    const canCreate = hasPermission(PERMISSIONS.CAR_FEATURE.CREATE, adminProfile);
    const canEdit = hasPermission(PERMISSIONS.CAR_FEATURE.EDIT, adminProfile);
    const canDelete = hasPermission(PERMISSIONS.CAR_FEATURE.DELETE, adminProfile);

    const breadcrumb = [{ link: '', linkText: 'Car Features' }];

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const resetForm = () => {
        setCategoryId('');
        setCategoryIdErr('');
        setName('');
        setNameErr('');
        setDescription('');
        setSortOrder('0');
        setStatus('1');
        setIcon('');
        setCurrentId('');
    };

    const handleClose = () => {
        setCreateModal(false);
        resetForm();
    };

    const getCategories = () => {
        featureCategoryDropdownService('').then(res => {
            if (res.status) {
                const rows = res.data && res.data.result;
                setCategories(Array.isArray(rows) ? rows : []);
            }
        }).catch(() => {
            setCategories([]);
        });
    };

    const buildQuery = (page = pageNo) => {
        let params = `?pageNo=${page}&count=${pageSize}`;
        if (search) params += `&search=${encodeURIComponent(search)}`;
        if (filterStatus !== '') params += `&status=${filterStatus}`;
        if (filterCategory) params += `&categoryId=${filterCategory}`;
        return params;
    };

    const getList = (page = pageNo) => {
        setLoader(true);
        carFeatureListService(buildQuery(page)).then(res => {
            setLoader(false);
            if (res.status) {
                const rows = res.data && res.data.result;
                setList(Array.isArray(rows) ? rows : []);
                setTotalPages(res.data.totalPages || 1);
                setTotalCount(res.data.totalCount || 0);
                setPageNo(res.data.currentPage || page);
            } else {
                showToast(getDisplayMessage(res.data, 'Failed to fetch features'), 'danger');
            }
        }).catch(() => {
            setLoader(false);
            showToast('Failed to fetch features', 'danger');
        });
    };

    useEffect(() => {
        getCategories();
        if (canView) {
            getList(1);
        } else {
            setLoader(false);
        }
    }, []);

    const getCategoryName = item => {
        if (item.categoryId && item.categoryId.name) {
            return item.categoryId.name;
        }
        return '-';
    };

    const handleValidate = () => {
        let validate = true;
        if (!categoryId) {
            setCategoryIdErr('Category is required');
            validate = false;
        }
        if (name === '' || name === undefined) {
            setNameErr('Feature name is required');
            validate = false;
        }
        return validate;
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (!handleValidate()) return;

        setIsDisabled(true);
        let params = {
            categoryId,
            name,
            description,
            sortOrder: Number(sortOrder),
            status: Number(status),
            icon,
            carFeatureId: currentId || ''
        };

        let _service = currentId ? carFeatureUpdateService : carFeatureCreateService;

        _service(params).then(res => {
            setIsDisabled(false);
            if (res.status) {
                showToast(currentId ? 'Feature updated successfully' : 'Feature created successfully');
                handleClose();
                getList(currentId ? pageNo : 1);
            } else {
                showToast(getDisplayMessage(res.data, 'Something went wrong'), 'danger');
            }
        }).catch(() => {
            setIsDisabled(false);
            showToast('Something went wrong', 'danger');
        });
    };

    const handleDeleteConfirm = () => {
        setLoader(true);
        carFeatureDeleteService({ carFeatureId: deleteId }).then(res => {
            setDeleteModal(false);
            if (res.status == 1) {
                showToast('Feature deleted successfully');
                getList(pageNo);
            } else {
                showToast(getDisplayMessage(res.data, 'Delete failed'), 'danger');
                setLoader(false);
            }
        }).catch(() => {
            setDeleteModal(false);
            setLoader(false);
            showToast('Delete failed', 'danger');
        });
    };

    const handleEdit = item => {
        setCreateModal(true);
        setCategoryId(item.categoryId && item.categoryId._id ? item.categoryId._id : item.categoryId);
        setName(item.name);
        setDescription(item.description || '');
        setSortOrder(String(item.sortOrder || 0));
        setStatus(String(item.status !== undefined ? item.status : 1));
        setIcon(item.icon || '');
        setCurrentId(item._id);
    };

    const handleView = item => {
        setViewData(item);
        setViewModal(true);

        carFeatureByIdService(`?carFeatureId=${item._id}`).then(res => {
            if (res.status && res.data && res.data.result) {
                setViewData(res.data.result);
            } else {
                showToast(getDisplayMessage(res.data, 'Could not load feature details'), 'danger');
            }
        }).catch(() => {
            showToast('Could not load feature details', 'danger');
        });
    };

    const handleStatusToggle = item => {
        if (!canEdit) return;
        carFeatureUpdateService({
            carFeatureId: item._id,
            status: item.status == 1 ? 0 : 1
        }).then(res => {
            if (res.status) {
                showToast('Status updated successfully');
                getList(pageNo);
            }
        });
    };

    if (!canView) {
        return (
            <div className="page_wrapper">
                <Breadcrumb breadcrumb={breadcrumb} />
                <div className="alert alert-warning mt-3">You do not have permission to view car features.</div>
            </div>
        );
    }

    return (
        <div className="page_wrapper">
            {toast.show ? (
                <div className={`alert alert-${toast.type} alert-dismissible fade show`} role="alert">
                    {toast.message}
                </div>
            ) : ''}

            <div className="search_bar">
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search features"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), getList(1))}
                />
                <i className="fa fa-search" />
                <select
                    className="form-control ml-2"
                    style={{ maxWidth: '180px' }}
                    value={filterCategory}
                    onChange={e => setFilterCategory(e.target.value)}
                >
                    <option value="">All Categories</option>
                    {categories.map(cat => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                </select>
                <select
                    className="form-control ml-2"
                    style={{ maxWidth: '160px' }}
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                >
                    <option value="">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                </select>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => getList(1)}>Search</button>
            </div>

            <Breadcrumb breadcrumb={breadcrumb} />

            <div className="justifydesign d-flex justify-content-between align-content-center page_header">
                <h2>Car Features</h2>
                {canCreate && (
                    <span className="btn btn-primary" onClick={() => { resetForm(); setCreateModal(true); }}>
                        Add Feature
                    </span>
                )}
            </div>

            <div className="table-responsive cm_card px-3 py-0 mb-3 cm_table_min_height">
                {loader ? <div className="loader_lg"><Loader /></div> : ''}
                <table className="table user-table table-hover align-items-center">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Feature Name</th>
                            <th>Category Name</th>
                            <th>Status</th>
                            <th>Created Date</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list && list.map((item, i) => (
                            <tr key={item._id}>
                                <td>{((pageNo - 1) * pageSize) + i + 1}</td>
                                <td>{item.name}</td>
                                <td>{getCategoryName(item)}</td>
                                <td>
                                    <span
                                        className={`badge ${item.status == 1 ? 'badge-success' : 'badge-secondary'} ${canEdit ? 'cursor-pointer' : ''}`}
                                        onClick={() => handleStatusToggle(item)}
                                    >
                                        {item.status == 1 ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>{moment(item.created).format('DD MMM, YYYY')}</td>
                                <td>
                                    <Dropdown alignRight>
                                        <Dropdown.Toggle className="no_btn"><i className="ellipsis-h" /></Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item as="button" onClick={() => handleView(item)}>View</Dropdown.Item>
                                            {canEdit && (
                                                <Dropdown.Item as="button" onClick={() => handleEdit(item)}>Edit</Dropdown.Item>
                                            )}
                                            {canDelete && (
                                                <Dropdown.Item as="button" onClick={() => { setDeleteId(item._id); setDeleteModal(true); }}>Delete</Dropdown.Item>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </td>
                            </tr>
                        ))}
                        {!list.length && !loader ? (
                            <tr><td colSpan="6" className="text-center">No features found</td></tr>
                        ) : ''}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 ? (
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <span>Showing {list.length} of {totalCount}</span>
                    <div>
                        <button className="btn btn-sm btn-outline-primary mr-2" disabled={pageNo <= 1} onClick={() => getList(pageNo - 1)}>Prev</button>
                        <span className="mx-2">Page {pageNo} of {totalPages}</span>
                        <button className="btn btn-sm btn-outline-primary ml-2" disabled={pageNo >= totalPages} onClick={() => getList(pageNo + 1)}>Next</button>
                    </div>
                </div>
            ) : ''}

            <Modal show={createModal} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{currentId ? 'Edit Feature' : 'Add Feature'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Category</label>
                            <select
                                value={categoryId}
                                onChange={e => (setCategoryId(e.target.value), setCategoryIdErr(''))}
                                className={`form-control ${categoryIdErr ? 'is-invalid' : ''} rounded-pill`}
                            >
                                <option value="">Select Category</option>
                                {categories.map(cat => (
                                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                                ))}
                            </select>
                            {categoryIdErr ? (
                                <OverlayTrigger placement="left" overlay={<Tooltip>{categoryIdErr}</Tooltip>}>
                                    <span className="error_tootltip" />
                                </OverlayTrigger>
                            ) : ''}
                        </div>

                        <div className="form-group">
                            <label>Feature Name</label>
                            <input
                                className={`form-control ${nameErr ? 'is-invalid' : ''} rounded-pill`}
                                type="text"
                                value={name}
                                maxLength="100"
                                onChange={e => (setName(e.target.value), setNameErr(''))}
                                placeholder="Enter feature name"
                            />
                            {nameErr ? (
                                <OverlayTrigger placement="left" overlay={<Tooltip>{nameErr}</Tooltip>}>
                                    <span className="error_tootltip" />
                                </OverlayTrigger>
                            ) : ''}
                        </div>

                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                className="form-control"
                                rows="3"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Enter description"
                            />
                        </div>

                        <div className="form-group">
                            <label>Sort Order</label>
                            <input
                                className="form-control rounded-pill"
                                type="number"
                                value={sortOrder}
                                onChange={e => setSortOrder(e.target.value)}
                                placeholder="Enter sort order"
                            />
                        </div>

                        <div className="form-group">
                            <label>Status</label>
                            <select
                                value={status}
                                onChange={e => setStatus(e.target.value)}
                                className="form-control rounded-pill"
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Icon (optional URL)</label>
                            <input
                                className="form-control rounded-pill"
                                type="text"
                                value={icon}
                                onChange={e => setIcon(e.target.value)}
                                placeholder="Enter icon URL"
                            />
                        </div>

                        <div className="text-center mt-3">
                            <button disabled={isDisabled} type="submit" className="btn btn-primary rounded-pill pl-5 pr-5">
                                Submit
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            <Modal show={viewModal} onHide={() => setViewModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>View Feature</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewData ? (
                        <div>
                            <p><strong>Name:</strong> {viewData.name}</p>
                            <p><strong>Slug:</strong> {viewData.slug}</p>
                            <p><strong>Category:</strong> {viewData.categoryId && viewData.categoryId.name ? viewData.categoryId.name : '-'}</p>
                            <p><strong>Description:</strong> {viewData.description || '-'}</p>
                            <p><strong>Sort Order:</strong> {viewData.sortOrder}</p>
                            <p><strong>Status:</strong> {viewData.status == 1 ? 'Active' : 'Inactive'}</p>
                            <p><strong>Created:</strong> {moment(viewData.created).format('DD MMM, YYYY')}</p>
                        </div>
                    ) : ''}
                </Modal.Body>
            </Modal>

            <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Confirmation</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to delete this feature?</Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setDeleteModal(false)}>Cancel</button>
                    <button className="btn btn-danger" onClick={handleDeleteConfirm}>Delete</button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

const mapStateToProps = state => ({
    adminProfile: (state.auth && (state.auth.adminProfile || state.auth.userProfile)) || null
});

export default connect(mapStateToProps)(CarFeature);
